import { useEffect, useMemo } from 'react';
import { z } from 'zod';
import { Button, Dialog, IconButton, IconName } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import {
    createZodFormResolver,
    FormInputField,
    LocalizedText,
    lt,
    showSuccessAlert,
    useFormControls,
    Validation,
} from '@mews/framework';
import { useRepositoryMutation, useInvalidateRepositoryQuery } from '@mews-commander/data-access';

interface Props {
    active: boolean;
    onClose: () => void;
    passkeyId: string;
    oldName: string;
}

enum FieldName {
    Name = 'name',
}

const renameSchema = z.object({
    [FieldName.Name]: Validation.String.Required,
});

export const RenamePasskeyDialog = ({ active, onClose, passkeyId, oldName }: Props) => {
    const invalidateQuery = useInvalidateRepositoryQuery();
    const { mutate: renamePasskey } = useRepositoryMutation('users', 'updatePasskey', {
        onSuccess: () => {
            void invalidateQuery('users', 'getRegisteredCredentials');

            showSuccessAlert({
                title: <LocalizedText textKey={TextKey.Success} />,
            });
        },
    });

    const defaultValues = useMemo(
        () => ({
            [FieldName.Name]: oldName,
        }),
        [oldName]
    );

    const { renderFormContent, submit, reset } = useFormControls({
        onSubmit: ({ name }) => {
            renamePasskey({ passkeyId, name: { value: name } });
            onClose();
        },
        resolver: createZodFormResolver(renameSchema),
        defaultValues,
    });

    useEffect(() => {
        reset(defaultValues);
    }, [reset, defaultValues]);

    const handleClose = () => {
        reset(defaultValues);
        onClose();
    };

    return renderFormContent(
        <Dialog
            active={active}
            onClose={handleClose}
            title={<LocalizedText textKey={TextKey.SettingsSecurityPasskeysRenameTitle} />}
            footer={{
                secondary: (
                    <Button onClick={handleClose} variant="tertiary" type="button">
                        <LocalizedText textKey={TextKey.GoBack} />
                    </Button>
                ),
                primary: (
                    <Button onClick={submit} variant="primary" type="submit">
                        <LocalizedText textKey={TextKey.Save} />
                    </Button>
                ),
            }}
            size="small"
        >
            <FormInputField name={FieldName.Name} label={lt(TextKey.SettingsSecurityPasskeysRenameLabel)} required />
        </Dialog>
    );
};

export const RenamePasskeyButton = ({ toggleRenameDialog }: { toggleRenameDialog: () => void }) => {
    return <IconButton icon={IconName.Edit} type="button" variant="ghost" size="small" onClick={toggleRenameDialog} />;
};
